import React, { useRef, useState } from "react";
import { Download, Upload, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { downloadCSV, toCSV, fromCSV } from "../lib/csv";

/**
 * Reusable Export + Import button pair.
 *
 * Props:
 *  - filename: base filename (e.g. "agents") → final "agents-YYYY-MM-DD.csv"
 *  - headers: string[]  CSV column order
 *  - items: array of rows to export
 *  - toRow(item): object keyed by header names (with nested fields JSON-stringified)
 *  - onImport(parsedRows): async handler that receives array of row objects
 *    (should return a { created, updated, skipped } summary)
 */
export default function ExportImport({ filename, headers, items, toRow, onImport, testid = "export-import" }) {
  const fileRef = useRef(null);
  const [importing, setImporting] = useState(false);

  const handleExport = () => {
    try {
      const rows = (items || []).map(toRow);
      const csv = toCSV(headers, rows);
      const stamp = new Date().toISOString().slice(0, 10);
      downloadCSV(`${filename}-${stamp}.csv`, csv);
      toast.success(`Exported ${rows.length} row${rows.length === 1 ? "" : "s"}`);
    } catch (e) {
      console.error(e);
      toast.error("Export failed");
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const { data } = fromCSV(text);
      if (data.length === 0) {
        toast.error("No rows found in CSV");
        return;
      }
      const summary = await onImport(data);
      const parts = [];
      if (summary?.created) parts.push(`${summary.created} created`);
      if (summary?.updated) parts.push(`${summary.updated} updated`);
      if (summary?.skipped) parts.push(`${summary.skipped} skipped`);
      toast.success(`Import complete — ${parts.join(", ") || `${data.length} rows`}`);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Import failed");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <input
        ref={fileRef}
        type="file"
        accept=".csv,text/csv"
        onChange={handleFile}
        className="hidden"
        data-testid={`${testid}-file`}
      />
      <Button
        variant="outline"
        onClick={() => fileRef.current?.click()}
        disabled={importing}
        className="h-10 rounded-xl"
        data-testid={`${testid}-import`}
      >
        {importing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
        Import
      </Button>
      <Button
        variant="outline"
        onClick={handleExport}
        className="h-10 rounded-xl"
        data-testid={`${testid}-export`}
      >
        <Download className="h-4 w-4 mr-1" /> Export
      </Button>
    </div>
  );
}
